import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Col, Row } from 'react-bootstrap';
import { CURRENCY_CHOICES } from '../../common_components/choices';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash/debounce';

const PurchaseDetailsModal = ({ show, onHide, item, refreshList }) => {
    console.log("Received item:", item);
    const token = localStorage.getItem('token');
    const userInfoContent = JSON.parse(localStorage.getItem('userInfo'));
    const [changedFields, setChangedFields] = useState({});

    // State to handle selected linked product and store
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedOffer, setSelectedOffer] = useState(null);

    const [purchaseDetails, setPurchaseDetails] = useState({
        name: '',
        product: '',
        store: '',
        offer: '',
        quantity: 1,
        purchased: false,
        purchase_price: '',
        purchase_price_currency: userInfoContent?.preferred_currency || 'USD', // Ensure this always has a fallback
        purchased_on: '', // Default to empty string
        notes: '',
    });

    const fetchUserFavoriteProducts = useCallback(async (searchTerm) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-products/?search=${searchTerm}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                    }
                }
            );

            return response.data.results.map(prod => ({
                value: prod.product_details.id,
                label: `${prod.product_details.name} - ${prod.product_details.product_brand.name}`,
            }));
        } catch (error) {
            console.error("Error fetching user favorite products:", error);
            return [];
        }
    }, [token]);

    const fetchUserFavoriteStores = useCallback(async (searchTerm) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-stores/?search=${searchTerm}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                    }
                }
            );

            return response.data.results.map(store => ({
                value: store.store_details.id,
                label: store.store_details ? String(store.store_details.name) : 'Unnamed Store',
            }));
        } catch (error) {
            console.error("Error fetching user favorite stores:", error);
            return [];
        }
    }, [token]);

    const fetchGroceryOffers = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-offers/`, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });
            return response.data.results.map(offer => ({
                value: offer.id,
                label: offer.name
            }));
        } catch (error) {
            console.error("Error fetching grocery offers:", error);
            return [];
        }
    }, [token]);

    // Initialize or update the selected product when item changes
    useEffect(() => {
        if (item) {
            setSelectedProduct(item?.linked_product ? {
                value: item.linked_product.id,
                label: `${item.linked_product.name} - ${item.linked_product.product_brand?.name || ''}`
            } : null);

            setSelectedStore(item?.linked_store ? {
                value: item.linked_store.id,
                label: `${item.linked_store.name} - ${item.linked_store.store_brand?.name || ''}`
            } : null);

            setSelectedOffer(item?.linked_offer ? {
                value: item.linked_offer.id,
                label: `${item.linked_offer.name} - ${item.linked_offer.offer_brand?.name || ''}`
            } : null);

            // Update purchaseDetails to reflect the linked product
            setPurchaseDetails(prevDetails => ({
                ...prevDetails,
                product: item?.product || '',
                store: item?.store || '',
                offer: item?.offer || '',
                name: item?.name || '',
                quantity: item?.quantity || 1,
                purchased: item?.purchased || false,
                purchase_price: item?.purchase_price || '',
                purchase_price_currency: item?.purchase_price_currency || userInfoContent?.preferred_currency || 'USD',
                purchased_on: item?.purchased_on ? new Date(item.purchased_on).toISOString() : '',
                notes: item?.notes || '',
            }));
        }
    }, [item, userInfoContent?.preferred_currency]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setPurchaseDetails(prev => ({ ...prev, [name]: value }));
        setChangedFields(prev => ({ ...prev, [name]: true }));
    }, []);

    const markItemAsPurchased = async () => {
        const apiUrl = `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/${item?.id}/`;
        const updatedDetails = {
            purchased: true,
            purchased_on: new Date().toISOString(),
        };
        console.log('Sending updated details:', updatedDetails);
        try {
            const response = await axios.patch(apiUrl, updatedDetails, {
                headers: { Authorization: `Token ${token}` },
            });
            if (response.status === 200) {
                refreshList();
                setPurchaseDetails(prev => ({
                    ...prev,
                    purchased: true,
                    purchased_on: new Date().toISOString()
                }));
                onHide();
            }
        } catch (error) {
            console.error('Response:', error.response);
        }
    };

    // Debounced function to fetch user's favorites
    const debouncedFetchProducts = debounce(fetchUserFavoriteProducts, 300);
    const debouncedFetchStores = debounce(fetchUserFavoriteStores, 300);
    const debouncedFetchGroceryOffers = debounce(fetchGroceryOffers, 300);

    const handleSubmit = async () => {
        const apiUrl = `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/${item?.id}/`;
        console.log('Changed Fields:', changedFields);
        console.log('Purchase Details:', purchaseDetails);
        const payload = Object.keys(changedFields).reduce((acc, key) => {
            if (changedFields[key]) {  // Only add the field if it has been changed
                acc[key] = purchaseDetails[key];  // This will include null values if delinked
            }
            return acc;
        }, {});

        console.log('Sending updated details:', payload);

        try {
            const response = await axios.patch(apiUrl, payload, {
                headers: { Authorization: `Token ${token}` },
            });
            if (response.status === 200) {
                refreshList();
                onHide();
            }
        } catch (error) {
            console.error('Error updating item:', error);
            console.error('Response:', error.response);
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Purchase Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" name="name" value={purchaseDetails?.name} disabled />
                    </Form.Group>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity</Form.Label>
                                <Form.Control type="number" name="quantity" value={purchaseDetails.quantity} onChange={handleInputChange} min="1" required />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Purchased On</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="purchased_on"
                                    value={purchaseDetails.purchased_on ? purchaseDetails.purchased_on.slice(0, 10) : ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Purchase Price</Form.Label>
                                <Form.Control type="text" name="purchase_price" value={purchaseDetails.purchase_price} onChange={handleInputChange} />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Currency</Form.Label>
                                <Form.Select name="purchase_price_currency" value={purchaseDetails.purchase_price_currency} onChange={handleInputChange}>
                                    {CURRENCY_CHOICES.map((choice) => (
                                        <option key={choice.value} value={choice.value}>
                                            {choice.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    {/* Link to favorite product */}
                    <Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Link to Favorite Product</Form.Label>
                            <AsyncSelect
                                value={selectedProduct}
                                loadOptions={debouncedFetchProducts}
                                onChange={setSelectedProduct}
                                placeholder="Select Favorite Product"
                                isSearchable
                                isClearable
                            />
                        </Form.Group>
                    </Row>
                    {/* Link to favorite store */}
                    <Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Bought From Favorite Store</Form.Label>
                            <AsyncSelect
                                value={selectedStore}
                                loadOptions={debouncedFetchStores}
                                onChange={setSelectedStore}
                                placeholder="Select Favorite Store"
                                isSearchable
                                isClearable
                            />
                        </Form.Group>
                    </Row>
                    {/* Link to an offer */}
                    <Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Link to Offer</Form.Label>
                            <AsyncSelect
                                value={selectedOffer}
                                loadOptions={debouncedFetchGroceryOffers} // This handles the async loading
                                onChange={setSelectedOffer}
                                placeholder="Select Offer"
                                isSearchable
                                isClearable
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="notes"
                                value={purchaseDetails.notes}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="info" onClick={() => handleSubmit()}>Save Details</Button>
                <Button
                    variant={purchaseDetails.purchased ? "success" : "primary"}
                    onClick={() => markItemAsPurchased()}
                    disabled={purchaseDetails.purchased} // Disable the button if the item is already purchased
                >
                    {purchaseDetails.purchased ? "Purchased" : "Mark as Purchased"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PurchaseDetailsModal;
